import { GetCoverageStatusCountsByOrgRequest } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { SchemaInsuranceVerificationAdapterServiceV2 } from '@frontend/schema';
import { useGetInsuranceAlertsByOrgIdOptions } from './types';

const queryKeys = {
  base: ['insurance'] as const,
  insuranceAlertsCount: (request: GetCoverageStatusCountsByOrgRequest) => [
    ...queryKeys.base,
    'insurance-alerts-count',
    request,
  ],
  practiceType: (locationId: string) => [...queryKeys.base, 'practice-type-list', locationId],
  nonDentalEligibilityReport: (locationId: string, requestId: string) => [
    ...queryKeys.base,
    'eligibility-report',
    locationId,
    requestId,
  ],
};

const LOCATION_ID_HEADER = 'Location-Id';
const getLocationIdHeader = (locationId?: string) => ({
  headers: locationId ? { [LOCATION_ID_HEADER]: locationId } : undefined,
});

export const useGetInsuranceAlertsByOrgId = (
  request: GetCoverageStatusCountsByOrgRequest,
  options: UseQueryOptions<useGetInsuranceAlertsByOrgIdOptions>
) => {
  const { orgId, locationIds, startDate, endDate } = request;
  return useQuery({
    queryKey: queryKeys.insuranceAlertsCount({ orgId, locationIds, startDate, endDate }),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetCoverageStatusCountsByOrg({
        orgId,
        locationIds,
        startDate,
        endDate,
      }),
    enabled: !!options.enabled,
  });
};

export const useGetPracticeTypeList = (locationId: string, enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.practiceType(locationId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetPVerifyPracticeType({}, getLocationIdHeader(locationId)),
    enabled: !!locationId && enabled,
    retry: false,
  });
};

export const useGetNonDentalInsuranceEligibilityReport = (
  locationId: string,
  requestId: string,
  options: UseQueryOptions<useGetInsuranceAlertsByOrgIdOptions>
) => {
  return useQuery({
    queryKey: queryKeys.nonDentalEligibilityReport(locationId, requestId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetInsuranceEligibilityReport(
        {
          requestId,
        },
        getLocationIdHeader(locationId)
      ),
    enabled: !!(locationId && requestId && options.enabled),
    retry: false,
  });
};

export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateGetPracticeTypeList: (locationId: string) => {
      queryClient.invalidateQueries(queryKeys.practiceType(locationId));
    },
  };
};
